import { TrackingService } from '../services/TrackingService'
import { PageViewed } from '../events/PageViewed'
import type { PageViewedArgs } from '../events/PageViewed'

/**
 * Hook for tracking page view events.
 *
 * Fires a PageViewed Cohesion event for the current page. Automatically
 * attaches `webContext` from `window._Cohesion` so callers only need to
 * supply `revisionId` and `pageName`. Use a ref guard at the call site to
 * prevent duplicate events on re-renders.
 *
 * @returns A function that fires the PageViewed tracking event
 *
 * @example
 * ```typescript
 * const trackPageViewed = usePageViewed()
 * const hasTracked = useRef(false)
 *
 * useEffect(() => {
 *   if (hasTracked.current) return
 *   hasTracked.current = true
 *   trackPageViewed({ revisionId, pageName: 'grid' })
 * }, [revisionId, trackPageViewed])
 * ```
 */
const usePageViewed =
  () =>
  (args: PageViewedArgs): void => {
    const webContext = typeof window !== 'undefined' ? window._Cohesion?.webContext : undefined
    TrackingService.track(new PageViewed({ ...args, webContext }))
  }

export default usePageViewed
